import React from "react";
import { ArrowUp } from "lucide-react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Hero from "./components/Hero";
import Logos from "./components/Logos";
import Features from "./components/Features";
import Quickstart from "./components/Quickstart";
import UseCases from "./components/UseCases";
import Pricing from "./components/Pricing";
import SectionBg from "./illustrations/SectionBg";

export default function App() {
  const initialized = React.useRef(false);
  const lastScrollY = React.useRef<number>(0);
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  React.useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    document.documentElement.classList.add('js-ready');
    document.documentElement.classList.add('force-anim');
    const elms = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (!('IntersectionObserver' in window)) {
      elms.forEach((e) => e.classList.add('reveal-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          (entry.target as HTMLElement).classList.add('reveal-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -1px 0px', threshold: 0 });
    elms.forEach((e) => {
      e.classList.add('reveal');
      requestAnimationFrame(() => io.observe(e));
    });
    return () => io.disconnect();
  }, []);
  React.useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      setShowScrollTop(y > 200);
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Initialize state based on initial position
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const scrollToTop = React.useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  return (
    <div className="relative min-h-screen bg-neutral-950 text-neutral-100 ps-fade-in">
      <div className="relative min-h-screen">
        <SectionBg intensity="vivid" className="absolute inset-0 z-0" />
        <Header />
        <main className="relative z-10">
          <Hero />
          <Logos />
          <Features />
          <Quickstart />
          <UseCases />
          <Pricing />
        </main>
      </div>
      <Footer />
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          aria-label="Scroll to top"
          className="fixed bottom-6 right-6 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition-transform hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/60"
        >
          <ArrowUp className="size-5" />
        </button>
      )}
    </div>
  );
}
